'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  addDays,
  addMonths,
  monthGrid,
  monthOfDay,
  timeOptions,
  weekdayOrder,
  type CalendarDay,
  type CalendarMonth,
} from '@icebreaker/shared/domain';
import { type TranslationKey } from '@icebreaker/shared/i18n';
import { useTranslation } from './i18n-context';
import styles from './date-time-picker.module.css';

const MONTH_KEYS: readonly TranslationKey[] = [
  'datePicker.month1',
  'datePicker.month2',
  'datePicker.month3',
  'datePicker.month4',
  'datePicker.month5',
  'datePicker.month6',
  'datePicker.month7',
  'datePicker.month8',
  'datePicker.month9',
  'datePicker.month10',
  'datePicker.month11',
  'datePicker.month12',
];

const WEEKDAY_SHORT_KEYS: readonly TranslationKey[] = [
  'datePicker.weekdayShort0',
  'datePicker.weekdayShort1',
  'datePicker.weekdayShort2',
  'datePicker.weekdayShort3',
  'datePicker.weekdayShort4',
  'datePicker.weekdayShort5',
  'datePicker.weekdayShort6',
];

const WEEKDAY_LONG_KEYS: readonly TranslationKey[] = [
  'datePicker.weekdayLong0',
  'datePicker.weekdayLong1',
  'datePicker.weekdayLong2',
  'datePicker.weekdayLong3',
  'datePicker.weekdayLong4',
  'datePicker.weekdayLong5',
  'datePicker.weekdayLong6',
];

export interface CalendarPanelProps {
  /** `time` kipinde ızgara hiç çizilmez; panel yalnız saat sütunudur. */
  readonly showCalendar?: boolean;
  /** Seçili gün (`YYYY-MM-DD`) ya da boş dize — boşsa ızgara bugüne açılır. */
  readonly selectedDay: string;
  /** Bugünün günü, ETKİNLİĞİN diliminde hesaplanmış. */
  readonly todayDay: CalendarDay;
  /** Seçilebilir en erken gün; altındaki hücreler kapalıdır. */
  readonly minDay?: CalendarDay;
  /** Seçilebilir en geç gün; üstündeki hücreler kapalıdır. */
  readonly maxDay?: CalendarDay;
  readonly onPickDay: (day: CalendarDay) => void;
  /** Saat sütunu yalnız `datetime` kipinde çizilir. */
  readonly time?: string;
  readonly minuteStep?: number;
  /** Seçili gün `minDay` ile aynıysa bu saatin altındakiler kapalıdır. */
  readonly minTime?: string;
  /** Seçili gün `maxDay` ile aynıysa bu saatin üstündekiler kapalıdır. */
  readonly maxTime?: string;
  readonly onPickTime?: (time: string) => void;
  readonly onClose: () => void;
}

/**
 * TAKVIM IZGARASI + SAAT SÜTUNU.
 *
 * KLAVYE (README 4.9): ızgaraya girildiğinde odak seçili güne oturur; ok
 * tuşları gün/hafta, PageUp/PageDown ay taşır, Home/End haftanın uçlarına
 * gider, Enter/Space seçer, Esc kapatır. Odak GEZİNEN tabIndex ile tek
 * hücrededir — 42 hücre Tab sırasına girmez.
 *
 * SAAT SÜTUNU AYNI DESENDEDİR (tech-lead 30.08): 96 seçenek Tab sırasını
 * şişiriyordu — klavye kullanıcısı takvimden çıkmak için 96 kez Tab'a
 * basıyordu. Sütun artık TEK durak: etkin seçenek `tabindex=0`, diğerleri
 * `-1`; ok tuşları seçenek, PageUp/PageDown bir saat (4 adım), Home/End
 * uçlar, Enter/Space seçer. Kapalı seçenekler atlanır.
 *
 * Izgara ayın DIŞINDAKİ günleri de çizer (sabit 6 satır): ay değişince panel
 * yüksekliği zıplamaz ve ay sınırındaki gün tek tıkla seçilir.
 */
export function CalendarPanel({
  showCalendar = true,
  selectedDay,
  todayDay,
  minDay,
  maxDay,
  onPickDay,
  time,
  minuteStep = 15,
  minTime,
  maxTime,
  onPickTime,
  onClose,
}: CalendarPanelProps) {
  const t = useTranslation();
  const weekStartsOn = t.locale === 'tr' ? 1 : 0;
  /* Seçim yoksa ızgara bugüne açılır; bugün izinli aralığın dışındaysa aralığın
     en yakın ucuna (gelecek bir etkinliğin ajandası bugünün ayında açılmaz). */
  const anchorDay =
    selectedDay ||
    (minDay !== undefined && todayDay < minDay
      ? minDay
      : maxDay !== undefined && todayDay > maxDay
        ? maxDay
        : todayDay);
  const [month, setMonth] = useState<CalendarMonth>(monthOfDay(anchorDay));
  const [focusedDay, setFocusedDay] = useState<CalendarDay>(anchorDay);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const timeListRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusRef = useRef(false);
  const shouldFocusTimeRef = useRef(false);
  const [rovingTime, setRovingTime] = useState<string | null>(null);

  const options = timeOptions(minuteStep);
  /* Saat tabanı: gün kipinde YALNIZ alt sınır gününde, saat kipinde her zaman
     uygulanır (o kipte gün yoktur). */
  const timeFloor =
    minTime !== undefined && (minDay === undefined || selectedDay === minDay) ? minTime : undefined;
  const timeCeiling =
    maxTime !== undefined && (maxDay === undefined || selectedDay === maxDay) ? maxTime : undefined;
  const timeEnabled = (option: string) =>
    (timeFloor === undefined || option >= timeFloor) &&
    (timeCeiling === undefined || option <= timeCeiling);
  const dayEnabled = (day: CalendarDay) =>
    (minDay === undefined || day >= minDay) && (maxDay === undefined || day <= maxDay);
  const openOptions = options.filter(timeEnabled);
  /* Gezinen seçenek: kullanıcının taşıdığı → seçili saat → ilk AÇIK seçenek. */
  const activeTime =
    rovingTime !== null && timeEnabled(rovingTime)
      ? rovingTime
      : time !== undefined && time !== '' && options.includes(time) && timeEnabled(time)
        ? time
        : (openOptions[0] ?? null);

  /*
   * Saat sütunu SEÇİLİ saate kaydırılır (yoksa ilk açık seçeneğe). Liste 96
   * satırdır; 00:00'da açılmak kullanıcıyı her seferinde elle kaydırmaya
   * zorluyordu. `scrollTop` yazılır, `scrollIntoView` DEĞİL — ikincisi sayfayı
   * da kaydırıp katmanı ekrandan iterdi.
   */
  useEffect(() => {
    const list = timeListRef.current;
    if (!list) return;
    const option =
      list.querySelector<HTMLElement>('[aria-selected="true"]') ??
      list.querySelector<HTMLElement>('button:not(:disabled)');
    if (option) list.scrollTop = option.offsetTop - list.offsetTop;
  }, [time]);

  /* Odak, ok tuşuyla taşınan güne TAŞINIR; ilk çizimde çalınmaz (panel açılışında
     odağı metin girişinden almak, yazmaya devam eden kullanıcıyı keserdi). */
  useEffect(() => {
    if (!shouldFocusRef.current) return;
    shouldFocusRef.current = false;
    const cell = gridRef.current?.querySelector<HTMLButtonElement>('[data-roving="true"]');
    cell?.focus();
  }, [focusedDay, month]);

  /* Odak, ok tuşuyla taşınan saate TAŞINIR; ilk çizimde çalınmaz. */
  useEffect(() => {
    if (!shouldFocusTimeRef.current) return;
    shouldFocusTimeRef.current = false;
    timeListRef.current?.querySelector<HTMLButtonElement>('[data-roving="true"]')?.focus();
  }, [rovingTime]);

  const moveTime = (index: number) => {
    if (openOptions.length === 0) return;
    const clamped = Math.max(0, Math.min(openOptions.length - 1, index));
    shouldFocusTimeRef.current = true;
    setRovingTime(openOptions[clamped] ?? null);
  };

  const onTimeKeyDown = (keyEvent: KeyboardEvent<HTMLDivElement>) => {
    const current = activeTime === null ? -1 : openOptions.indexOf(activeTime);
    const steps: Readonly<Record<string, number>> = {
      ArrowDown: 1,
      ArrowRight: 1,
      ArrowUp: -1,
      ArrowLeft: -1,
      /* Bir saat = 60/minuteStep adım (15 dk'da dört satır). */
      PageDown: Math.max(1, Math.round(60 / minuteStep)),
      PageUp: -Math.max(1, Math.round(60 / minuteStep)),
    };
    const step = steps[keyEvent.key];
    if (step !== undefined) {
      keyEvent.preventDefault();
      moveTime(current + step);
      return;
    }
    if (keyEvent.key === 'Home' || keyEvent.key === 'End') {
      keyEvent.preventDefault();
      moveTime(keyEvent.key === 'Home' ? 0 : openOptions.length - 1);
      return;
    }
    if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
      /* Seçim AÇIKÇA ele alınır: `role="option"` taşıyan düğmede tarayıcının
         Enter → click yoluna güvenilmez (ızgara hücresinde ölçülen kusur). */
      keyEvent.preventDefault();
      if (activeTime !== null && onPickTime) onPickTime(activeTime);
      return;
    }
    if (keyEvent.key === 'Escape') {
      keyEvent.preventDefault();
      onClose();
    }
  };

  const moveFocus = (day: CalendarDay) => {
    shouldFocusRef.current = true;
    setFocusedDay(day);
    setMonth(monthOfDay(day));
  };

  const onGridKeyDown = (keyEvent: KeyboardEvent<HTMLDivElement>) => {
    const steps: Readonly<Record<string, number>> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const step = steps[keyEvent.key];
    if (step !== undefined) {
      keyEvent.preventDefault();
      moveFocus(addDays(focusedDay, step));
      return;
    }
    if (keyEvent.key === 'Home' || keyEvent.key === 'End') {
      keyEvent.preventDefault();
      const offset = (new Date(`${focusedDay}T00:00:00Z`).getUTCDay() - weekStartsOn + 7) % 7;
      moveFocus(addDays(focusedDay, keyEvent.key === 'Home' ? -offset : 6 - offset));
      return;
    }
    if (keyEvent.key === 'PageUp' || keyEvent.key === 'PageDown') {
      keyEvent.preventDefault();
      const next = addMonths(month, keyEvent.key === 'PageUp' ? -1 : 1);
      shouldFocusRef.current = true;
      setMonth(next);
      setFocusedDay(`${next}-${focusedDay.slice(8)}`);
      return;
    }
    if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
      /*
       * SEÇİM AÇIKÇA ELE ALINIR, yerel düğme davranışına GÜVENİLMEZ: hücre
       * `role="gridcell"` taşıdığı için tarayıcının Enter → click yolu kanıt
       * koşumunda tetiklenmedi (ilk koşumda "Enter seçmiyor" olarak ölçüldü).
       * Kapalı gün burada da seçilemez — kural tek yerde.
       */
      keyEvent.preventDefault();
      if (dayEnabled(focusedDay)) onPickDay(focusedDay);
      return;
    }
    if (keyEvent.key === 'Escape') {
      keyEvent.preventDefault();
      onClose();
    }
  };

  const cells = monthGrid(month, weekStartsOn);
  const monthLabel = `${t.t(MONTH_KEYS[Number(month.slice(5, 7)) - 1] as TranslationKey)} ${month.slice(0, 4)}`;

  return (
    <div className={styles.panel}>
      {showCalendar ? (
        <div className={styles.calendar}>
          <div className={styles.monthBar}>
            <button
              type="button"
              className={styles.monthNav}
              aria-label={t.t('datePicker.previousMonth')}
              onClick={() => setMonth(addMonths(month, -1))}
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className={styles.monthLabel} aria-live="polite">
              {monthLabel}
            </span>
            <button
              type="button"
              className={styles.monthNav}
              aria-label={t.t('datePicker.nextMonth')}
              onClick={() => setMonth(addMonths(month, 1))}
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          <div className={styles.weekdays} aria-hidden="true">
            {weekdayOrder(weekStartsOn).map((weekday) => (
              <span key={weekday} className={styles.weekday}>
                {t.t(WEEKDAY_SHORT_KEYS[weekday] as TranslationKey)}
              </span>
            ))}
          </div>
          <div
            ref={gridRef}
            role="grid"
            aria-label={t.t('datePicker.calendarLabel')}
            className={styles.grid}
            onKeyDown={onGridKeyDown}
          >
            {cells.map((cell) => {
              const disabled = !dayEnabled(cell.day);
              const isSelected = cell.day === selectedDay;
              const roving = cell.day === focusedDay;
              return (
                <button
                  key={cell.day}
                  type="button"
                  role="gridcell"
                  data-roving={roving ? 'true' : undefined}
                  data-testid={`day-${cell.day}`}
                  className={[
                    styles.day,
                    cell.inMonth ? '' : styles.dayOutside,
                    cell.day === todayDay ? styles.dayToday : '',
                    isSelected ? styles.daySelected : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  tabIndex={roving ? 0 : -1}
                  disabled={disabled}
                  aria-disabled={disabled || undefined}
                  aria-selected={isSelected}
                  aria-label={`${cell.dayOfMonth} ${t.t(MONTH_KEYS[Number(cell.day.slice(5, 7)) - 1] as TranslationKey)} ${cell.day.slice(0, 4)}, ${t.t(WEEKDAY_LONG_KEYS[cell.weekday] as TranslationKey)}`}
                  onFocus={() => setFocusedDay(cell.day)}
                  onClick={() => onPickDay(cell.day)}
                >
                  {cell.dayOfMonth}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {onPickTime ? (
        <div className={styles.times}>
          <span className={styles.timesTitle}>{t.t('datePicker.timeLabel')}</span>
          <div
            ref={timeListRef}
            role="listbox"
            aria-label={t.t('datePicker.timeListLabel')}
            className={styles.timeList}
            onKeyDown={onTimeKeyDown}
          >
            {options.map((option) => {
              const disabled = !timeEnabled(option);
              const roving = option === activeTime;
              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  data-roving={roving ? 'true' : undefined}
                  data-testid={`time-${option}`}
                  className={[styles.timeOption, option === time ? styles.timeSelected : '']
                    .filter(Boolean)
                    .join(' ')}
                  /* 96 seçenek TEK durak: Tab sırasında yalnız gezinen seçenek var. */
                  tabIndex={roving ? 0 : -1}
                  aria-selected={option === time}
                  disabled={disabled}
                  onFocus={() => setRovingTime(option)}
                  onClick={() => onPickTime(option)}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
